import React, { FunctionComponent, useState, useEffect } from "react"
import { Icon, Alert } from "@kuali/pure-ui"

interface Props {
  isAlertOpen: boolean
  message?: string
  color?: string
  iconName?: string
}

/*
  Per `pure-ui` docs - 
  Alerts also have a prominent message and relation actions but do not support a title like modals. 
  Also, like modal alerts, alerts remain on the screen until a user has take an action to dismiss it. 
  However, when an alert is presented on the screen, it does not prevent the user from interacting with 
  the rest of the page like modal alerts. Alerts appear at the top of the page as well.
  https://kualico.github.io/pure-ui/docs-alert#alert
*/
const ReactAlert: FunctionComponent<Props> = ({
  isAlertOpen = false,
  message = "",
  color = "green",
  iconName = "alert-open"
}) => {
  // We use internal state to update Modal props as changes are also watched via
  // an Event Listener - this is anti-pattern in React (we should rely on consumed
  // props only) but necessary as we convert legacy app
  const [isOpen, setIsOpen] = useState(false)
  const [alertMessage, setAlertMessage] = useState(message)
  const [alertIcon, setAlertIcon] = useState(iconName)
  const [alertColor, setAlertColor] = useState(color)

  const fadeOutModal = () => {
    const timer = setTimeout(() => {
      // closes open modal after 3 seconds
      setIsOpen(false)
    }, 3000)
    return () => clearTimeout(timer)
  }

  // callback with empty array ensures that `onMount` we set open prop,
  // ensuring that DOM is ready at correct point to show modal
  useEffect(() => {
    setIsOpen(isAlertOpen)

    // watch custom event which may fire via outside Ajax calls to refresh modal
    // visiblity and content
    window.addEventListener("noticeChange", function(event: CustomEvent) {
      if (event.detail) {
        setIsOpen(event.detail.isOpen)
        setAlertMessage(event.detail.message)
        setAlertIcon(event.detail.iconName)
        setAlertColor(event.detail.color)

        // when modal has been set to open set to fade out
        if (event.detail.isOpen) {
          fadeOutModal()
        }
      }
    })
  }, [])

  useEffect(() => {
    // when modal has changed to open set to fade out
    if (isAlertOpen) {
      fadeOutModal()
    }
  }, [isAlertOpen])

  return (
    <React.Fragment>
      <Alert
        id={"alert"}
        color={alertColor}
        icon={<Icon name={alertIcon} />}
        message={alertMessage}
        primaryButtonLabel={"Ok"}
        closeButtonLabel="Close"
        isOpen={isOpen}
        autoFocus={false}
        onPrimaryButtonClicked={(e) => setIsOpen(false)}
        onCloseButtonClicked={(e) => setIsOpen(false)}
      />
    </React.Fragment>
  )
}
export default ReactAlert
